import { gql } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { Notification, PaginatedNotifications } from '@this+that/models';

interface GetPaginatedNotificationsData {
  getPaginatedNotifications: PaginatedNotifications;
}

interface MarkNotificationAsReadData {
  markNotificationAsRead: Notification;
}

interface ClearReadNotificationsData {
  clearReadNotifications: boolean;
}

const GET_PAGINATED_NOTIFICATIONS = gql`
  query GetPaginatedNotifications($limit: Int!, $exclusiveStartKey: String, $ascending: Boolean) {
    getPaginatedNotifications(
      limit: $limit
      exclusiveStartKey: $exclusiveStartKey
      ascending: $ascending
    ) {
      notifications {
        id
        userId
        createdAt
        priority
        displayType
        wasRead
        message
        redirectUrl
        updatedBy
        sourceService
        kind
        data
      }
      exclusiveStartKey
    }
  }
`;

export function useGetPaginatedNotifications() {
  const [fetchNotifications, { loading }] =
    useLazyQuery<GetPaginatedNotificationsData>(GET_PAGINATED_NOTIFICATIONS, {
      fetchPolicy: 'network-only',
    });

  const getPaginatedNotifications = async (
    limit: number,
    exclusiveStartKey?: string | null,
    ascending?: boolean,
  ): Promise<PaginatedNotifications> => {
    const { data } = await fetchNotifications({
      variables: { limit, exclusiveStartKey, ascending },
    });

    return (
      data?.getPaginatedNotifications ?? {
        notifications: [],
        exclusiveStartKey: null,
      }
    );
  };

  return { getPaginatedNotifications, loading };
}

interface GetUnreadNotificationsData {
  getUnreadNotifications: PaginatedNotifications;
}

interface GetReadNotificationsData {
  getReadNotifications: PaginatedNotifications;
}

const GET_UNREAD_NOTIFICATIONS = gql`
  query GetUnreadNotifications($limit: Int!, $exclusiveStartKey: String, $ascending: Boolean) {
    getUnreadNotifications(
      limit: $limit
      exclusiveStartKey: $exclusiveStartKey
      ascending: $ascending
    ) {
      notifications {
        id
        userId
        createdAt
        priority
        displayType
        wasRead
        message
        redirectUrl
        updatedBy
        sourceService
        kind
        data
      }
      exclusiveStartKey
    }
  }
`;

export function useGetUnreadNotifications() {
  const [fetchUnreadNotifications, { loading }] =
    useLazyQuery<GetUnreadNotificationsData>(GET_UNREAD_NOTIFICATIONS, {
      fetchPolicy: 'network-only',
    });

  const getUnreadNotifications = async (
    limit: number,
    exclusiveStartKey?: string | null,
    ascending: boolean = false,
  ): Promise<PaginatedNotifications> => {
    const { data } = await fetchUnreadNotifications({
      variables: { limit, exclusiveStartKey, ascending },
    });

    return (
      data?.getUnreadNotifications ?? {
        notifications: [],
        exclusiveStartKey: null,
      }
    );
  };

  return { getUnreadNotifications, loading };
}

const GET_READ_NOTIFICATIONS = gql`
  query GetReadNotifications($limit: Int!, $exclusiveStartKey: String, $ascending: Boolean) {
    getReadNotifications(
      limit: $limit
      exclusiveStartKey: $exclusiveStartKey
      ascending: $ascending
    ) {
      notifications {
        id
        userId
        createdAt
        priority
        displayType
        wasRead
        message
        redirectUrl
        updatedBy
        sourceService
        kind
        data
      }
      exclusiveStartKey
    }
  }
`;

export function useGetReadNotifications() {
  const [fetchReadNotifications, { loading }] =
    useLazyQuery<GetReadNotificationsData>(GET_READ_NOTIFICATIONS, {
      fetchPolicy: 'network-only',
    });

  const getReadNotifications = async (
    limit: number,
    exclusiveStartKey?: string | null,
    ascending: boolean = false,
  ): Promise<PaginatedNotifications> => {
    const { data } = await fetchReadNotifications({
      variables: { limit, exclusiveStartKey, ascending },
    });

    return (
      data?.getReadNotifications ?? {
        notifications: [],
        exclusiveStartKey: null,
      }
    );
  };

  return { getReadNotifications, loading };
}

const MARK_NOTIFICATION_AS_READ = gql`
  mutation markNotificationAsRead($id: String!) {
    markNotificationAsRead(id: $id) {
      id
      userId
      createdAt
      priority
      displayType
      wasRead
      message
      redirectUrl
      updatedBy
      sourceService
      kind
      data
    }
  }
`;

export function useMarkNotificationAsRead() {
  const [markNotificationAsReadMutation, { loading, error }] =
    useMutation<MarkNotificationAsReadData>(MARK_NOTIFICATION_AS_READ);

  const markNotificationAsRead = async (id: string) => {
    const { data } = await markNotificationAsReadMutation({
      variables: { id },
    });

    return data?.markNotificationAsRead;
  };

  return { markNotificationAsRead, loading, error };
}

const CLEAR_READ_NOTIFICATIONS = gql`
  mutation clearReadNotifications {
    clearReadNotifications
  }
`;

export function useClearReadNotifications() {
  const [clearReadNotificationsMutation, { loading, error }] =
    useMutation<ClearReadNotificationsData>(CLEAR_READ_NOTIFICATIONS);

  const clearReadNotifications = async () => {
    const { data } = await clearReadNotificationsMutation();

    return data?.clearReadNotifications ?? false;
  };

  return { clearReadNotifications, loading, error };
}
